import { isArray, isString } from 'lodash';

import {
  type AdHocVariableFilter,
  type BaseVariableModel,
  type UrlQueryValue,
  VariableRefresh,
  type VariableWithOptions,
} from '@grafana/data';
import { logError } from '@grafana/runtime';
import { isCustomVariableValue, type SceneVariable, type VariableValue } from '@grafana/scenes';
import { type VariableModel } from '@grafana/schema';
import { variableAdapters } from 'app/features/variables/adapters';
import { ALL_VARIABLE_TEXT, ALL_VARIABLE_VALUE } from 'app/features/variables/constants';
import { hasCurrent, hasOptions, isQuery } from 'app/features/variables/guard';
import { getVariablesByKey } from 'app/features/variables/state/selectors';
import { getVariablesFromUrl } from 'app/features/variables/utils';

import { type Report } from '../../types';

export const REPORT_VARIABLE_ERROR_SOURCE = 'extensions.reports.templateVariables';

type UnsupportedVariableLogContext = {
  operation: 'apply' | 'serialize';
  variableType: string;
  reason: string;
};

/**
 * Records an unsupported-variable fallback for Faro via logError (pushError), and
 * surfaces a console warning for local page debugging.
 * Callers continue without throwing so report save/load is not blocked.
 */
export function logUnsupportedVariableFallback(message: string, context: UnsupportedVariableLogContext) {
  const fullContext = {
    source: REPORT_VARIABLE_ERROR_SOURCE,
    ...context,
  };
  logError(new Error(message), fullContext);
  console.warn(message, fullContext);
}

function isVariableValueSingle(value: unknown): boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    (typeof value === 'object' && value !== null && isCustomVariableValue(value as VariableValue))
  );
}

function isVariableValue(value: unknown): value is VariableValue {
  if (Array.isArray(value)) {
    return value.every(isVariableValueSingle);
  }
  return isVariableValueSingle(value);
}

/**
 * Reads `state.value` only when present and typed as a Scene VariableValue.
 * Avoids unsafe casts on the untyped SceneVariableState bag.
 */
export function readSceneVariableStateValue(variable: SceneVariable): VariableValue | undefined {
  if (!('value' in variable.state)) {
    return undefined;
  }

  const candidate = (variable.state as { value: unknown }).value;
  return isVariableValue(candidate) ? candidate : undefined;
}

/**
 * Convert variable values to a map like:
 * { "myVar": ["var1", "var2"] }
 * @param variables
 */
export const toReportVariables = (variables?: BaseVariableModel[]): Record<string, string[]> => {
  if (!variables?.length) {
    return {};
  }

  return Object.fromEntries(
    variables.map((variable) => {
      try {
        const { getValueForUrl } = variableAdapters.get(variable.type);
        const value = getValueForUrl(variable);
        return [variable.name, Array.isArray(value) ? value : [value]];
      } catch (error) {
        console.error(`Error getting value for variable %s:`, variable.name, error);
        return [variable.name, []];
      }
    })
  );
};

export const toReportSceneVariables = (variables?: SceneVariable[]): Record<string, string[]> => {
  if (!variables?.length) {
    return {};
  }

  const variablesFromUrl = getVariablesFromUrl();

  return Object.fromEntries(
    variables.map((variable) => {
      const { name } = variable.state;
      const queryValue = variablesFromUrl[name];
      const queryValueArray = (Array.isArray(queryValue) ? queryValue : [queryValue]).map((q) => q?.toString() ?? '');
      return [name, queryValueArray];
    })
  );
};

export const applyDefaultVariablesV2 = (
  variables: VariableModel[],
  reportVariables?: Report['templateVars']
): VariableModel[] => {
  if (!reportVariables || !Object.keys(reportVariables).length) {
    return variables;
  }

  return variables.map((variable) => {
    const reportVariable = reportVariables[variable.name];
    // Missing override only — empty arrays are valid (cleared ad hoc filters / multi values).
    if (reportVariable == null) {
      return variable;
    }

    if (variable.type === 'system' || variable.type === 'snapshot') {
      return variable;
    }

    if (variable.type === 'adhoc') {
      return {
        ...variable,
        filters: toFilters(reportVariable),
      };
    }

    // Structural check: value-based variables expose `current` and/or `options`.
    // Dashboard JSON may omit `options`, so do not require it (see applyDefaultVariableWithOptions).
    if (hasCurrent(variable as BaseVariableModel) || hasOptions(variable as BaseVariableModel)) {
      return applyDefaultVariableWithOptions(variable, reportVariable);
    }

    // Keep going so report save/load is not blocked; leave the variable unchanged.
    logUnsupportedVariableFallback(
      `Reporting: Falling back for unsupported variable type ${variable.type}; leaving variable unchanged`,
      {
        operation: 'apply',
        variableType: variable.type,
        reason: 'unsupported_shape',
      }
    );
    return variable;
  });
};

/**
 * Applies report variable values onto a persisted dashboard variable model.
 * Dashboard JSON may omit `options` (schema: options?), so normalize before use.
 */
const toVariableOptionScalar = (value: string | string[]): string => {
  return Array.isArray(value) ? (value[0] ?? '') : value;
};

const applyDefaultVariableWithOptions = (variable: VariableModel, reportVariable: string[]): VariableModel => {
  const options = variable.options ?? [];
  const values = reportVariable
    .map((str) => options.find((opt) => opt.value === str) || { text: str, value: str })
    .filter(Boolean);

  const isMultiVariable = Boolean(variable.multi);
  // Empty [] is valid for multi (clear selection). Single-value has no values[0] to apply.
  if (!isMultiVariable && values.length === 0) {
    return variable;
  }

  const current = isMultiVariable
    ? {
        text: values.map((val) => toVariableOptionScalar(val.text)),
        value: values.map((val) => {
          const optionValue = toVariableOptionScalar(val.value);
          return optionValue === ALL_VARIABLE_TEXT ? ALL_VARIABLE_VALUE : optionValue;
        }),
        selected: true,
      }
    : {
        text: toVariableOptionScalar(values[0].text),
        value: (() => {
          const optionValue = toVariableOptionScalar(values[0].value);
          return optionValue === ALL_VARIABLE_TEXT ? ALL_VARIABLE_VALUE : optionValue;
        })(),
        selected: true,
      };

  return {
    ...variable,
    current: {
      ...variable.current,
      ...current,
    },
    options: options.map((option) => ({
      ...option,
      selected: typeof option.value === 'string' && reportVariable.includes(option.value),
    })),
  };
};

export const applyDefaultVariables = (
  variables: BaseVariableModel[],
  reportVariables?: Report['templateVars'],
  repeatValuesSet?: Set<string>
): VariableWithOptions[] | BaseVariableModel[] => {
  if (!reportVariables || !Object.keys(reportVariables).length) {
    return variables.map((v) => ({ ...v, usedInRepeat: repeatValuesSet?.has(v.name) }));
  }

  return variables.map((variable) => {
    const reportVariable = reportVariables[variable.name];
    if (!reportVariable || !hasOptions(variable)) {
      return variable;
    }
    const values = reportVariable
      .map((str) => variable.options.find((opt) => opt.value === str) || { text: str, value: str })
      .filter(Boolean);

    const isMultiVariable = 'multi' in variable && variable.multi;
    return {
      ...variable,
      usedInRepeat: repeatValuesSet?.has(variable.name),
      current: {
        ...variable.current,
        text: isMultiVariable ? values.map((val) => val?.text) : values[0].text,
        value: isMultiVariable ? values.map((val) => val?.value) : values[0].value,
      },
      options: variable.options.map((option) => ({
        ...option,
        selected: typeof option.value === 'string' && reportVariable.includes(option.value),
      })),
    };
  });
};

export const collectVariables = () => {
  const variablePrefix = 'var-';
  const urlParams = new URLSearchParams(window.location.search);
  const variables: Record<string, string[]> = {};

  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith(variablePrefix)) {
      const newKey = key.replace(variablePrefix, '');
      variables[newKey] = [...(variables[newKey] || []), value];
    }
  }

  return variables;
};

export const refreshOnTimeRange = (variables: BaseVariableModel[]) => {
  return variables.some((variable) => isQuery(variable) && variable.refresh === VariableRefresh.onTimeRangeChanged);
};

export const getPreviewVariables = (
  reportVariables?: Record<string, string[]>,
  dashboardUid?: string
): Array<[string, string[]]> => {
  if (!reportVariables) {
    return [];
  }

  const dashboardVariables = dashboardUid ? getVariablesByKey(dashboardUid) : [];
  const defaultVariables = applyDefaultVariables(dashboardVariables, reportVariables);

  return Object.entries(reportVariables).map(([key, value]) => {
    let currentValue = value;
    const variable = defaultVariables.find((v) => v.name === key);
    if (variable && hasCurrent(variable)) {
      currentValue = Array.isArray(variable.current.text) ? variable.current.text : [variable.current.text];
    }
    return [key, currentValue];
  });
};

// this logic is extracted from adhoc adapter so we can correctly persist the variable as the backend needs
// public/app/features/variables/adhoc/urlParser.ts
export const convertAdHocVariableToDTO = (filters: AdHocVariableFilter[]): string[] => {
  return filters.map((filter) => toArray(filter).map(escapeDelimiter).join('|'));
};

const toFilters = (value: UrlQueryValue): AdHocVariableFilter[] => {
  if (isArray(value)) {
    const values = value;
    return values.map(toFilter).filter(isFilter);
  }

  const filter = toFilter(value);
  return filter === null ? [] : [filter];
};

function escapeDelimiter(value: string | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return /\|/g[Symbol.replace](value, '__gfp__');
}

function unescapeDelimiter(value: string | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return /__gfp__/g[Symbol.replace](value, '|');
}

function toArray(filter: AdHocVariableFilter): string[] {
  return [filter.key, filter.operator, filter.value];
}

function toFilter(value: string | number | boolean | undefined | null): AdHocVariableFilter | null {
  if (!isString(value) || value.length === 0) {
    return null;
  }

  const parts = value.split('|').map(unescapeDelimiter);

  return {
    key: parts[0],
    operator: parts[1],
    value: parts[2],
  };
}

function isFilter(filter: AdHocVariableFilter | null): filter is AdHocVariableFilter {
  return filter !== null && isString(filter.value);
}
